'use client';

import { useMemo, useState, useEffect, useRef} from 'react';

import clsx from 'clsx';

import PropTypes from 'prop-types';

import arrowDown from '../../../assets/designSystem/icons/simple-arrow/down.svg';
import './styles.css';

const useOutsideClick = (callback) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current?.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [ref]);

  return ref;
};

function KitoSelect(props) {
  const [showOptions, setShowOptions] = useState(false);
  const collapseRef = useOutsideClick(() => setShowOptions(false));
  const isDisabled = Boolean(props.disabled);

  useEffect(() => {
    if (isDisabled && showOptions) setShowOptions(false);
  }, [isDisabled, showOptions]);

  const shownValue = useMemo(() => {
    return props.options.find((option) => option.value === props.value)?.label || null;
  }, [props.value]);

  const dynamicStyle = {
    width: props.fullWidth ? 'calc(100% - 50px)' : props.width ? `${props.width}` : '250px',
  };

  const dynamicOptionsStyle = {
    height: showOptions ? `${props.options.length * 40 + 1}px` : '0',
    width: props.fullWidth ? '100%' : props.width ? `${props.width + 48}px` : '298px',
  };
  
  const selectOption = (event, option) => {
    event.stopPropagation();
    props.onChange(option);
    setShowOptions(false);
  };

  const clearSelection = () => {
    props.onChange(null);
    setShowOptions(!showOptions);
  };

  return (
    <>
      {props.label && <p className="select-component__label">{props.label}</p>}
      <div
        ref={collapseRef}
        className={clsx(
          'select-component',
          showOptions && 'select-component__active',
          isDisabled && 'select-component__disabled'
        )}
        style={dynamicStyle}
        onClick={() => {
          if (isDisabled) return;
          setShowOptions((prev) => !prev);
        }}
      >
        <p
          className={clsx(
            'select-component__selected-option',
            !shownValue && 'select-component__selected-option__placeholder'
          )}
        >
          {shownValue || props.placeholder || ''}
        </p>
        <div className="select-component__actions">
          <img
            className={clsx(
              'select-component__arrow',
              'svg-primary-3',
              showOptions && 'select-component__arrow-active'
            )}
            src={arrowDown}
            alt="arrow-down"
          />
          {props.clear && props.value && !isDisabled && (
            <img
              src=""
              alt="close"
              className={clsx('select-component__close-icon', 'select-component__arrow')}
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
            />
          )}
        </div>
        <div
          className={clsx('select-component__options', showOptions && 'select-component__show-options')}
          style={dynamicOptionsStyle}
        >
          {props.options.map((option) => (
            <p className="select-component__option" key={option.value} onClick={(e) => selectOption(e, option.value)}>
              {option.label}
            </p>
          ))}
        </div>
      </div>
    </>
  );
}

KitoSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any.isRequired,
  options: PropTypes.arrayOf({
    label: PropTypes.string,
    value: PropTypes.any,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  width: PropTypes.number,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  placeholder: PropTypes.string,
  clear: PropTypes.bool,
};

export default KitoSelect;
